import axios from 'axios';

// Base API configuration
const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://glamapp.api.webspeed.online/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  verifyToken: (token) =>
    api.get('/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    }),
  updateProfile: (profileData) => api.put('/auth/me', profileData),
  changePassword: (passwordData) =>
    api.put('/auth/change-password', passwordData),
};

// Admin API - Dashboard and Analytics
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getUsers: (params = {}) => api.get('/admin/users', { params }),
  createUser: (userData) => api.post('/users', userData),
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/users/${id}`),
  updateUserStatus: (id, isActive, reason = '') =>
    api.put(`/admin/users/${id}/status`, { isActive, reason }),
  getServices: (params = {}) => api.get('/admin/services', { params }),
  updateServiceStatus: (id, isActive, reason = '') =>
    api.put(`/admin/services/${id}/status`, { isActive, reason }),
  getAppointments: (params = {}) => api.get('/admin/appointments', { params }),
  getReviews: (params = {}) => api.get('/admin/reviews', { params }),
  getAnalytics: (period = '30d') =>
    api.get('/admin/analytics', { params: { period } }),
  exportData: (type, params = {}) =>
    api.get(`/admin/export/${type}`, { params }),
  getSalonProfile: () => api.get('/admin/salon-profile'),
  updateSalonProfile: (profileData) =>
    api.put('/admin/salon-profile', profileData),

  // Salon Management
  getSalons: (params = {}) => api.get('/admin/salons', { params }),
  getSalon: (id) => api.get(`/admin/salons/${id}`),
  createSalon: (salonData) => api.post('/admin/salons', salonData),
  updateSalon: (id, salonData) => api.put(`/admin/salons/${id}`, salonData),
  deleteSalon: (id) => api.delete(`/admin/salons/${id}`),
  toggleSalonStatus: (id, isActive, reason = '') =>
    api.put(`/admin/salons/${id}/status`, { isActive, reason }),

  // Salon assignment endpoints
  assignSalonOwner: (salonId, ownerId) => {
    return api.put(`/admin/salons/${salonId}/assign-owner`, { ownerId });
  },

  unassignSalonOwner: (salonId) => {
    return api.delete(`/admin/salons/${salonId}/assign-owner`);
  },

  getAvailableOwners: (search = '') => {
    return api.get(`/admin/users/available-owners${search ? `?search=${search}` : ''}`);
  },

  // Salon image management
  uploadSalonImages: (salonId, formData) => 
    api.post(`/admin/salons/${salonId}/images`, formData),
  
  deleteSalonImage: (salonId, imageIndex) => 
    api.delete(`/admin/salons/${salonId}/images/${imageIndex}`),
  
  setPrimarySalonImage: (salonId, imageIndex) => 
    api.put(`/admin/salons/${salonId}/images/${imageIndex}/primary`),
  
  updateSalonImageTitle: (salonId, imageIndex, title) => 
    api.put(`/admin/salons/${salonId}/images/${imageIndex}`, { title }),
  
  getSalonImages: (salonId) => 
    api.get(`/admin/salons/${salonId}/images`),

  // Service Management (Admin)
  getAdminServices: (params = {}) =>
    api.get('/admin/services', { params }),
  getAdminService: (id) => api.get(`/admin/services/${id}`),
  createAdminService: (serviceData) =>
    api.post('/admin/admin-services', serviceData),
  updateAdminService: (id, serviceData) =>
    api.put(`/admin/admin-services/${id}`, serviceData),
  deleteAdminService: (id) => api.delete(`/admin/admin-services/${id}`),

  // Service Assignment to Salons
  getUnassignedServices: (params = {}) =>
    api.get('/admin/services/unassigned', { params }),
  assignServicesToSalon: (salonId, data) =>
    api.post(`/admin/salons/${salonId}/assign-services`, data),

  // Push Notifications
  getPushAudienceCount: (params = {}) =>
    api.get('/admin/notifications/audience-count', { params }),
  sendPushNotification: (data) =>
    api.post('/admin/notifications/send', data),

  // Payouts
  getPayoutRequests: (params = {}) =>
    api.get('/payouts/admin/all', { params }),
  updatePayoutStatus: (id, data) =>
    api.put(`/payouts/admin/${id}`, data),
};

// Services API (for regular service operations)
export const servicesAPI = {
  getAll: (params = {}) => api.get('/services', { params }),
  getById: (id) => api.get(`/services/${id}`),
  create: (serviceData) => api.post('/services', serviceData),
  update: (id, serviceData) => api.put(`/services/${id}`, serviceData),
  delete: (id) => api.delete(`/services/${id}`),
  search: (params = {}) => api.get('/services/search', { params }),
  getCategories: () => api.get('/services/categories'),
  getByProvider: (providerId) => api.get(`/services/provider/${providerId}`),
  getMyServices: () => api.get('/services/my-services'),
  toggleStatus: (id) => api.put(`/services/${id}/toggle-status`),
};

// Appointments API (for regular appointment operations)
export const appointmentsAPI = {
  getAll: (params = {}) => api.get('/appointments', { params }),
  getById: (id) => api.get(`/appointments/${id}`),
  create: (appointmentData) => api.post('/appointments', appointmentData),
  update: (id, appointmentData) =>
    api.put(`/appointments/${id}`, appointmentData),
  cancel: (id, reason = '') =>
    api.put(`/appointments/${id}/cancel`, { reason }),
  confirm: (id) => api.put(`/appointments/${id}/status`, { status: 'confirmed' }),
  complete: (id) => api.put(`/appointments/${id}/status`, { status: 'completed' }),
  updateStatus: (id, status, notes = '') =>
    api.put(`/appointments/${id}/status`, { status, notes }),
  getMyAppointments: () => api.get('/appointments/my-appointments'),
  getAvailableSlots: (serviceId, date) =>
    api.get('/appointments/available-slots', {
      params: { serviceId, date },
    }),
  getStats: (period = '30d') =>
    api.get('/appointments/stats', { params: { period } }),
};

// Reviews API
export const reviewsAPI = {
  getAll: (params = {}) => api.get('/reviews', { params }),
  getById: (id) => api.get(`/reviews/${id}`),
  create: (reviewData) => api.post('/reviews', reviewData),
  update: (id, reviewData) => api.put(`/reviews/${id}`, reviewData),
  delete: (id) => api.delete(`/reviews/${id}`),
  getByService: (serviceId, params = {}) =>
    api.get(`/reviews/service/${serviceId}`, { params }),
  getByUser: (userId, params = {}) =>
    api.get(`/reviews/user/${userId}`, { params }),
  getMyReviews: () => api.get('/reviews/my-reviews'),
  moderate: (id, status) => api.put(`/reviews/${id}/moderate`, { status }),
};

// Users API (for user management)
export const usersAPI = {
  getAll: (params = {}) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, userData) => api.put(`/users/${id}`, userData),
  delete: (id) => api.delete(`/users/${id}`),
  getPreferences: () => api.get('/users/preferences'),
  updatePreferences: (preferences) =>
    api.put('/users/preferences', preferences),
  getStatistics: () => api.get('/users/statistics'),
  deactivate: (reason = '') => api.put('/users/deactivate', { reason }),
  reactivate: () => api.put('/users/reactivate'),
};

// Upload API
export const uploadAPI = {
  uploadServiceImage: (serviceId, formData) =>
    api.post(`/upload/service/${serviceId}`, formData),
  deleteServiceImage: (serviceId, imageId) =>
    api.delete(`/upload/service/${serviceId}/${imageId}`),
  uploadMultiple: (formData) =>
    api.post('/upload/multiple', formData),
  getSignature: (params) => api.post('/upload/signature', params),
  validateImage: (imageData) => api.post('/upload/validate', imageData),
  getTransformUrl: (params) => api.get('/upload/transform', { params }),
};

export default api;
